import { DeleteIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CaregiverId, components } from "../../../shared/schema/schema";
import { DAYS_MONDAY_FIRST, hours, shiftMinutes, title } from "./servicePlanWeek.utils";
import { DraftShift } from "./useScheduleEditorDraft";

type CoServicePlanImportCaregiver = components["schemas"]["CoServicePlanImportCaregiver"];
type CoServicePlanImportDuty = components["schemas"]["CoServicePlanImportDuty"];
type CoServicePlanImportLine = components["schemas"]["CoServicePlanImportLine"];

interface Props {
  /** Only the caregivers this submission named: nobody else may be scheduled on it. */
  caregivers: CoServicePlanImportCaregiver[];
  duties: CoServicePlanImportDuty[];
  lines: CoServicePlanImportLine[];
  shift: DraftShift;
  onChange: (patch: Partial<DraftShift>) => void;
  onRemove: () => void;
}

/**
 * One shift of the week a coordinator is building.
 *
 * The service code is the field the rest of the card hangs off: a shift bills under
 * one service line, so the line decides which duties can be on it, and — with the
 * caregiver — whether the pairing is one the sender validated. That last part is
 * spelled out here rather than left to the findings dialog, because a coordinator
 * who can see "nothing validated on H0038" while choosing has no reason to submit a
 * week that will come back refused.
 *
 * The duration is derived from the two times and never typed. There is no third
 * field that can disagree with them.
 */
export default function ScheduleEditorShiftCard(props: Props) {
  const { caregivers, duties, lines, shift } = props;

  const minutes = shiftMinutes(shift.startTime, shift.endTime);
  const caregiver = caregivers.find((x) => x.caregiverId === shift.caregiverId) ?? null;

  // The duties of the chosen line, each listed once however many times the sender
  // stated it. Nothing is selectable until a line is chosen, which is the point:
  // a duty means nothing without the code it is performed under.
  const lineDuties =
    shift.serviceCode === null
      ? []
      : dedupeByDutyCode(duties.filter((duty) => duty.serviceCode === shift.serviceCode));

  // A duty the shift carries that the chosen line does not offer — a seeded shift
  // whose service code could not be resolved, or one whose line has just changed
  // underneath a duties patch. Shown so it can be seen and taken off.
  const strayDuties = shift.duties.filter(
    (duty) => !lineDuties.some((lineDuty) => lineDuty.dutyCode === duty.dutyCode)
  );

  const carries = (dutyCode: number) => shift.duties.some((duty) => duty.dutyCode === dutyCode);

  const occurrencesOf = (dutyCode: number) =>
    shift.duties.find((duty) => duty.dutyCode === dutyCode)?.occurrences ?? 0;

  // Taking a duty on starts it at once a shift; taking it off drops the count with
  // it, so a duty the coordinator re-adds does not come back at whatever it was
  // before they removed it.
  const toggleDuty = (dutyCode: number, isChecked: boolean) =>
    props.onChange({
      duties: isChecked
        ? [...shift.duties, { dutyCode, occurrences: 1 }]
        : shift.duties.filter((duty) => duty.dutyCode !== dutyCode),
    });

  // How many times this shift performs the duty. A day's occurrences live on ONE
  // shift -- the scheduler stopped splitting them into a visit each -- so this is
  // the only place the count can be said, and the server derives the plan of care's
  // visitsPerWeek and timesPerDay from it.
  const setOccurrences = (dutyCode: number, occurrences: number) =>
    props.onChange({
      duties: shift.duties.map((duty) =>
        duty.dutyCode === dutyCode ? { ...duty, occurrences } : duty
      ),
    });

  return (
    <Box
      borderColor={shift.isNew ? "blue.300" : undefined}
      borderWidth="1px"
      p={3}
      rounded="md"
      shadow="xs"
    >
      <Flex align="flex-end" gap={3} wrap="wrap">
        <FormControl maxW="140px">
          <FormLabel fontSize="xs" mb={1}>
            Day
          </FormLabel>
          <Select
            size="sm"
            value={shift.dayOfWeek}
            onChange={(event) => props.onChange({ dayOfWeek: event.target.value })}
          >
            {DAYS_MONDAY_FIRST.map((day) => (
              <option key={day} value={day}>
                {title(day)}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl maxW="130px">
          <FormLabel fontSize="xs" mb={1}>
            Start
          </FormLabel>
          <Input
            size="sm"
            /* SHIFT_INCREMENT_MINUTES, in seconds: the picker steps by five minutes and
               the browser refuses anything between. The server refuses it too -- it is
               the authority, and it never rounds a typed time -- but a coordinator
               should not have to be told. */
            step={300}
            type="time"
            value={shift.startTime}
            onChange={(event) => props.onChange({ startTime: event.target.value })}
          />
        </FormControl>
        <FormControl maxW="130px">
          <FormLabel fontSize="xs" mb={1}>
            End
          </FormLabel>
          <Input
            isInvalid={minutes === null}
            size="sm"
            step={300}
            type="time"
            value={shift.endTime}
            onChange={(event) => props.onChange({ endTime: event.target.value })}
          />
        </FormControl>
        {/* Derived, never entered. */}
        <Box pb={1}>
          <Text color="gray.500" fontSize="xs">
            Duration
          </Text>
          <Text color={minutes === null ? "red.500" : undefined} fontWeight="semibold">
            {minutes === null ? "End must be after the start" : hours(minutes)}
          </Text>
        </Box>
        <Spacer />
        <IconButton
          aria-label="Remove this shift"
          colorScheme="red"
          icon={<DeleteIcon />}
          size="sm"
          variant="ghost"
          onClick={props.onRemove}
        />
      </Flex>

      <Flex gap={3} mt={3} wrap="wrap">
        <FormControl maxW="220px">
          <FormLabel fontSize="xs" mb={1}>
            Service code
          </FormLabel>
          <Select
            isInvalid={shift.serviceCode === null}
            placeholder="Choose a line"
            size="sm"
            value={shift.serviceCode ?? ""}
            onChange={(event) =>
              props.onChange({ serviceCode: event.target.value === "" ? null : event.target.value })
            }
          >
            {lines.map((line) => (
              <option key={line.serviceCode} value={line.serviceCode}>
                {line.serviceCode}
                {line.authorizationId === null
                  ? " · no authorization"
                  : ` · auth #${line.authorizationId}`}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl maxW="380px">
          <FormLabel fontSize="xs" mb={1}>
            Caregiver
          </FormLabel>
          <Select
            isInvalid={shift.caregiverId === null}
            placeholder="Choose a caregiver"
            size="sm"
            value={shift.caregiverId ?? ""}
            onChange={(event) =>
              props.onChange({
                caregiverId:
                  event.target.value === "" ? null : (Number(event.target.value) as CaregiverId),
              })
            }
          >
            {caregivers.map((option) => (
              <option key={option.caregiverId} value={option.caregiverId}>
                {caregiverLabel(option, shift.serviceCode)}
              </option>
            ))}
          </Select>
        </FormControl>
      </Flex>

      <Box mt={3}>
        <Text color="gray.500" fontSize="xs" mb={1}>
          Duties
        </Text>
        {shift.serviceCode === null ? (
          <Text color="gray.500" fontSize="sm">
            Choose a service code to see the duties that can be performed on this shift.
          </Text>
        ) : lineDuties.length === 0 && strayDuties.length === 0 ? (
          <Text color="gray.500" fontSize="sm">
            The plan named no duties on {shift.serviceCode}.
          </Text>
        ) : (
          <VStack align="stretch" spacing={1}>
            {lineDuties.map((duty) => (
              <Flex key={duty.dutyCode} align="center" gap={2} wrap="wrap">
                <Checkbox
                  isChecked={carries(duty.dutyCode)}
                  onChange={(event) => toggleDuty(duty.dutyCode, event.target.checked)}
                >
                  <Text fontSize="sm">
                    {duty.dutyCode}
                    {duty.label !== null && ` · ${duty.label}`}
                  </Text>
                </Checkbox>
                {/* How many times, and only once the duty is actually on the shift:
                    a count beside an unticked duty would invite entering one and
                    expecting the duty to appear. */}
                {carries(duty.dutyCode) && (
                  <Flex align="center" gap={1}>
                    <Text color="gray.500" fontSize="xs">
                      &times;
                    </Text>
                    <NumberInput
                      aria-label={`Times ${duty.dutyCode} is performed on this shift`}
                      maxW="72px"
                      min={1}
                      size="xs"
                      value={occurrencesOf(duty.dutyCode)}
                      onChange={(_, valueAsNumber) =>
                        setOccurrences(
                          duty.dutyCode,
                          Number.isNaN(valueAsNumber) ? 1 : Math.max(1, valueAsNumber)
                        )
                      }
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </Flex>
                )}
                {/* The pairing the verifier will refuse, said here instead. */}
                {carries(duty.dutyCode) &&
                  caregiver !== null &&
                  shift.serviceCode !== null &&
                  !validates(caregiver, shift.serviceCode, duty.dutyCode) && (
                    <Badge colorScheme="orange">not validated for this caregiver</Badge>
                  )}
              </Flex>
            ))}
            {strayDuties.map((duty) => (
              <Flex key={duty.dutyCode} align="center" gap={2} wrap="wrap">
                <Checkbox isChecked onChange={() => toggleDuty(duty.dutyCode, false)}>
                  <Text fontSize="sm">{duty.dutyCode}</Text>
                </Checkbox>
                <Badge colorScheme="red">not a duty of {shift.serviceCode}</Badge>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
}

/**
 * Who they are, and what the sender says they may do on the line being scheduled.
 *
 * In the option itself because that is where the choice is made: a native select
 * shows nothing but its text, and "nothing validated on H0038" read while choosing
 * saves a submit that comes back refused.
 */
function caregiverLabel(caregiver: CoServicePlanImportCaregiver, serviceCode: string | null) {
  const name = `${caregiver.firstName ?? ""} ${caregiver.lastName ?? ""}`.trim();
  const named = name === "" ? `Caregiver #${caregiver.caregiverId}` : name;

  if (serviceCode === null) {
    const count = caregiver.validatedDuties.length;
    return `${named} · ${count} validated ${count === 1 ? "duty" : "duties"}`;
  }

  const codes = [
    ...new Set(
      caregiver.validatedDuties
        .filter((duty) => duty.serviceCode === serviceCode)
        .map((duty) => duty.dutyCode)
    ),
  ].sort((a, b) => a - b);

  return codes.length === 0
    ? `${named} · nothing validated on ${serviceCode}`
    : `${named} · validated on ${serviceCode}: ${codes.join(", ")}`;
}

function validates(
  caregiver: CoServicePlanImportCaregiver,
  serviceCode: string,
  dutyCode: number
): boolean {
  return caregiver.validatedDuties.some(
    (duty) => duty.serviceCode === serviceCode && duty.dutyCode === dutyCode
  );
}

function dedupeByDutyCode(duties: CoServicePlanImportDuty[]): CoServicePlanImportDuty[] {
  const seen = new Map<number, CoServicePlanImportDuty>();
  for (const duty of duties) {
    if (!seen.has(duty.dutyCode)) {
      seen.set(duty.dutyCode, duty);
    }
  }
  return [...seen.values()];
}
